"use client";

import { useState, useCallback, useMemo } from "react";
import { WebsiteNav } from "@/components/website-nav";
import { PageHeader } from "@/components/page-header";
import { payrollApi, type CalculationResponse } from "@/lib/api";
import {
  formatInputValue,
  parseInputValue,
  formatMoney as formatCurrency,
  generateId,
} from "@/lib/format";
import {
  Loader2,
  Trash2,
} from "lucide-react";

interface EarningRow {
  id: string;
  label: string;
  amount: string;
}

interface DeductionRow {
  id: string;
  label: string;
  amount: string;
}

export default function PayslipPage() {
  // Employee details
  const [employeeName, setEmployeeName] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [kraPin, setKraPin] = useState("");
  const [payrollNumber, setPayrollNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [payrollPeriod, setPayrollPeriod] = useState(() => {
    const now = new Date();
    return now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  });

  // Salary
  const [basicSalary, setBasicSalary] = useState("");
  const [nonCashBenefits, setNonCashBenefits] = useState("");
  const [pensionContribution, setPensionContribution] = useState("");

  // Custom rows
  const [customEarnings, setCustomEarnings] = useState<EarningRow[]>([]);
  const [customDeductions, setCustomDeductions] = useState<DeductionRow[]>([]);

  // Calculation
  const [calculation, setCalculation] = useState<CalculationResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const addEarning = () => {
    setCustomEarnings((prev) => [
      ...prev,
      { id: generateId(), label: "", amount: "" },
    ]);
  };

  const removeEarning = (id: string) => {
    setCustomEarnings((prev) => prev.filter((e) => e.id !== id));
  };

  const updateEarning = (id: string, field: "label" | "amount", value: string) => {
    setCustomEarnings((prev) =>
      prev.map((e) =>
        e.id === id
          ? { ...e, [field]: field === "amount" ? formatInputValue(value) : value }
          : e
      )
    );
  };

  const addDeduction = () => {
    setCustomDeductions((prev) => [
      ...prev,
      { id: generateId(), label: "", amount: "" },
    ]);
  };

  const removeDeduction = (id: string) => {
    setCustomDeductions((prev) => prev.filter((d) => d.id !== id));
  };

  const updateDeduction = (id: string, field: "label" | "amount", value: string) => {
    setCustomDeductions((prev) =>
      prev.map((d) =>
        d.id === id
          ? { ...d, [field]: field === "amount" ? formatInputValue(value) : value }
          : d
      )
    );
  };

  const totalCustomEarnings = useMemo(
    () => customEarnings.reduce((sum, e) => sum + parseInputValue(e.amount), 0),
    [customEarnings]
  );

  const totalCustomDeductions = useMemo(
    () => customDeductions.reduce((sum, d) => sum + parseInputValue(d.amount), 0),
    [customDeductions]
  );

  const handleCalculate = useCallback(async () => {
    setError(null);
    setIsCalculating(true);
    try {
      const gross = parseInputValue(basicSalary) + totalCustomEarnings;
      const result = await payrollApi.calculateSalary({
        calculationType: "GROSS_TO_NET",
        grossSalary: gross,
        nonCashBenefits: parseInputValue(nonCashBenefits),
        pensionContribution: parseInputValue(pensionContribution),
      });
      setCalculation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Calculation failed");
      setCalculation(null);
    } finally {
      setIsCalculating(false);
    }
  }, [basicSalary, nonCashBenefits, pensionContribution, totalCustomEarnings]);

  const handleDownloadPdf = useCallback(async () => {
    if (!calculation) return;
    setIsDownloading(true);
    try {
      const blob = await payrollApi.generatePayslipPdf({
        employeeName: employeeName || "Employee",
        idNumber: idNumber || undefined,
        kraPin: kraPin || undefined,
        payrollNumber: payrollNumber || undefined,
        companyName: companyName || undefined,
        payrollPeriod: payrollPeriod || undefined,
        basicSalary: parseInputValue(basicSalary),
        nonCashBenefits: parseInputValue(nonCashBenefits) || undefined,
        pensionContribution: parseInputValue(pensionContribution) || undefined,
        customEarnings: customEarnings
          .filter((e) => parseInputValue(e.amount) > 0)
          .map((e) => ({ label: e.label || "Other Earning", amount: parseInputValue(e.amount) })),
        customDeductions: customDeductions
          .filter((d) => parseInputValue(d.amount) > 0)
          .map((d) => ({ label: d.label || "Other Deduction", amount: parseInputValue(d.amount) })),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip-${(employeeName || "employee").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download payslip");
    } finally {
      setIsDownloading(false);
    }
  }, [calculation, employeeName, idNumber, kraPin, payrollNumber, companyName, payrollPeriod, basicSalary, nonCashBenefits, pensionContribution, customEarnings, customDeductions]);

  // Computed values for preview
  const basic = parseInputValue(basicSalary);
  const grossSalary = calculation?.grossSalary ?? 0;
  const nssf = calculation?.deductions?.nssf ?? 0;
  const shif = calculation?.deductions?.nhif ?? 0;
  const housingLevy = calculation?.deductions?.housingLevy ?? 0;
  const paye = calculation?.deductions?.paye ?? 0;
  const pension = parseInputValue(pensionContribution);
  const ncb = parseInputValue(nonCashBenefits);
  const statutoryTotal = nssf + shif + housingLevy + paye;
  const totalDeductions = statutoryTotal + totalCustomDeductions + (pension > 0 ? pension : 0);
  const netPay = calculation ? (calculation.netSalary ?? 0) - totalCustomDeductions : 0;

  return (
    <div className="min-h-screen bg-[#F3F6FA]">
      <WebsiteNav />

      <PageHeader
        eyebrow="Generator"
        title="Payslip Generator"
        description="Enter employee details on the left, preview and download the payslip on the right. Supports custom earnings and deductions for a complete payslip document."
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-12 lg:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            {/* ─── LEFT: Input Form ─── */}
            <div className="space-y-8 overflow-y-auto lg:max-h-[calc(100vh-180px)] lg:pr-6">
              {/* Employee Details */}
              <div>
                <div className="mono mb-4 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.13em] text-slate-400">
                  Employee details
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div className="col-span-2">
                    <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      Full name
                    </label>
                    <input
                      type="text"
                      value={employeeName}
                      onChange={(e) => setEmployeeName(e.target.value)}
                      placeholder="John Doe"
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                  <div>
                    <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      ID number
                    </label>
                    <input
                      type="text"
                      value={idNumber}
                      onChange={(e) => setIdNumber(e.target.value)}
                      placeholder="12345678"
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                  <div>
                    <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      KRA PIN
                    </label>
                    <input
                      type="text"
                      value={kraPin}
                      onChange={(e) => setKraPin(e.target.value)}
                      placeholder="A123456789B"
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                  <div>
                    <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      Payroll no.
                    </label>
                    <input
                      type="text"
                      value={payrollNumber}
                      onChange={(e) => setPayrollNumber(e.target.value)}
                      placeholder="EMP-001"
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                  <div>
                    <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      Company name
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="SekaniPay"
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      Payroll period
                    </label>
                    <input
                      type="text"
                      value={payrollPeriod}
                      onChange={(e) => setPayrollPeriod(e.target.value)}
                      placeholder="May 2026"
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                </div>
              </div>

              {/* Salary */}
              <div>
                <div className="mono mb-4 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.13em] text-slate-400">
                  Salary
                </div>
                <div className="space-y-5">
                  <div>
                    <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      Basic salary (KES) *
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={basicSalary}
                      onChange={(e) => setBasicSalary(formatInputValue(e.target.value))}
                      placeholder="100,000"
                      className="w-full border-b-2 border-[#DCE3EA] bg-transparent pb-2 font-[family-name:var(--font-source-serif)] text-[28px] font-medium text-slate-950 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                        Non-cash benefits
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={nonCashBenefits}
                        onChange={(e) =>
                          setNonCashBenefits(formatInputValue(e.target.value))
                        }
                        placeholder="0"
                        className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                      />
                    </div>
                    <div>
                      <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                        Pension contribution
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pensionContribution}
                        onChange={(e) =>
                          setPensionContribution(formatInputValue(e.target.value))
                        }
                        placeholder="0"
                        className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Earnings */}
              <div>
                <div className="mono mb-4 flex items-center justify-between border-b border-[#DCE3EA] pb-2">
                  <span className="text-[9px] uppercase tracking-[.13em] text-slate-400">Additional earnings</span>
                  <button
                    onClick={addEarning}
                    className="text-[11px] font-medium text-[#2348C7] transition hover:text-[#18369B]"
                  >
                    + Add
                  </button>
                </div>
                {customEarnings.length === 0 ? (
                  <p className="py-3 text-center text-[12px] text-slate-400">
                    No additional earnings. Click Add to include allowances, bonuses, etc.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {customEarnings.map((earning) => (
                      <div key={earning.id} className="flex items-end gap-3">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={earning.label}
                            onChange={(e) =>
                              updateEarning(earning.id, "label", e.target.value)
                            }
                            placeholder="e.g. House Allowance"
                            className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[13px] text-slate-900 outline-none placeholder:text-slate-300 focus:border-[#4B91F7]"
                          />
                        </div>
                        <div className="w-32">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={earning.amount}
                            onChange={(e) =>
                              updateEarning(earning.id, "amount", e.target.value)
                            }
                            placeholder="0"
                            className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-right text-[13px] font-medium text-slate-900 outline-none placeholder:text-slate-300 focus:border-[#4B91F7] mono"
                          />
                        </div>
                        <button
                          onClick={() => removeEarning(earning.id)}
                          className="mb-1 p-1 text-slate-400 transition-colors hover:text-red-500"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Custom Deductions */}
              <div>
                <div className="mono mb-4 flex items-center justify-between border-b border-[#DCE3EA] pb-2">
                  <span className="text-[9px] uppercase tracking-[.13em] text-slate-400">Additional deductions</span>
                  <button
                    onClick={addDeduction}
                    className="text-[11px] font-medium text-red-600 transition hover:text-red-700"
                  >
                    + Add
                  </button>
                </div>
                {customDeductions.length === 0 ? (
                  <p className="py-3 text-center text-[12px] text-slate-400">
                    No additional deductions. Click Add to include loans, advances, etc.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {customDeductions.map((deduction) => (
                      <div key={deduction.id} className="flex items-end gap-3">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={deduction.label}
                            onChange={(e) =>
                              updateDeduction(deduction.id, "label", e.target.value)
                            }
                            placeholder="e.g. Loan Repayment"
                            className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[13px] text-slate-900 outline-none placeholder:text-slate-300 focus:border-[#4B91F7]"
                          />
                        </div>
                        <div className="w-32">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={deduction.amount}
                            onChange={(e) =>
                              updateDeduction(deduction.id, "amount", e.target.value)
                            }
                            placeholder="0"
                            className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-right text-[13px] font-medium text-slate-900 outline-none placeholder:text-slate-300 focus:border-[#4B91F7] mono"
                          />
                        </div>
                        <button
                          onClick={() => removeDeduction(deduction.id)}
                          className="mb-1 p-1 text-slate-400 transition-colors hover:text-red-500"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                disabled={!basicSalary || parseInputValue(basicSalary) <= 0 || isCalculating}
                className="ink-button w-full border border-slate-950 bg-slate-950 px-5 py-3 text-[13px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isCalculating ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Calculating…
                  </span>
                ) : (
                  "Generate Payslip"
                )}
              </button>

              {error && (
                <div className="flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* ─── RIGHT: Payslip Preview ─── */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="square-card border border-[#DCE3EA] bg-white">
                {/* Preview Header */}
                <div className="flex items-center justify-between border-b border-[#DCE3EA] px-5 py-3">
                  <span className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">
                    Payslip preview
                  </span>
                  {calculation && (
                    <div className="flex items-center gap-3 print:hidden">
                      <button
                        onClick={handlePrint}
                        className="ink-button border border-slate-950 bg-slate-950 px-3 py-1.5 text-[11px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7]"
                      >
                        Print / Save PDF
                      </button>
                      <button
                        onClick={handleDownloadPdf}
                        disabled={isDownloading}
                        className="ink-button border border-[#DCE3EA] px-3 py-1.5 text-[11px] font-medium text-slate-600 transition hover:bg-[#F3F6FA] disabled:opacity-50"
                      >
                        {isDownloading ? (
                          <Loader2 className="inline h-3.5 w-3.5 animate-spin" />
                        ) : (
                          "Download"
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Payslip Document */}
                <div className="p-5" id="payslip-printable">
                  <div className="relative mx-auto max-w-md border border-[#DCE3EA] bg-white p-6 text-xs print:max-w-none print:border-[1px] print:shadow-none">
                    {/* Watermark */}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
                      <span className="rotate-[-35deg] select-none text-4xl font-bold tracking-widest text-slate-100">
                        {(companyName || "SEKANIPAY").toUpperCase()}
                      </span>
                    </div>

                    {/* Header */}
                    <div className="relative mb-4 flex items-start justify-between border-b border-[#DCE3EA] pb-3">
                      <div>
                        <h3 className="text-base font-medium text-slate-950">
                          {companyName || "SekaniPay"}
                        </h3>
                        <p className="mono mt-0.5 text-[9px] uppercase tracking-[.1em] text-slate-400">
                          Period: {payrollPeriod}
                        </p>
                      </div>
                      <span className="mono text-[10px] uppercase tracking-[.15em] text-slate-400">
                        Payslip
                      </span>
                    </div>

                    {/* Employee Info */}
                    <div className="relative mb-4 space-y-2 border-b border-[#E8E7E1] pb-3">
                      <div className="grid grid-cols-[80px_1fr] gap-2 text-[12px]">
                        <span className="text-slate-400">Employee</span>
                        <span className="font-medium text-slate-900">
                          {employeeName || "—"}
                        </span>
                      </div>
                      {idNumber && (
                        <div className="grid grid-cols-[80px_1fr] gap-2 text-[12px]">
                          <span className="text-slate-400">ID Number</span>
                          <span className="mono text-slate-700">{idNumber}</span>
                        </div>
                      )}
                      {kraPin && (
                        <div className="grid grid-cols-[80px_1fr] gap-2 text-[12px]">
                          <span className="text-slate-400">KRA PIN</span>
                          <span className="mono text-slate-700">{kraPin}</span>
                        </div>
                      )}
                      {payrollNumber && (
                        <div className="grid grid-cols-[80px_1fr] gap-2 text-[12px]">
                          <span className="text-slate-400">Payroll No.</span>
                          <span className="mono text-slate-700">{payrollNumber}</span>
                        </div>
                      )}
                    </div>

                    {/* Earnings */}
                    <div className="relative mb-4">
                      <div className="mono mb-2 grid grid-cols-[1fr_100px] gap-3 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.08em] text-slate-400">
                        <span>Earnings</span>
                        <span className="text-right">Amount</span>
                      </div>
                      <div className="text-[12px] text-slate-700">
                        <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                          <span>Basic Salary</span>
                          <span className="mono text-right font-medium">
                            {basic > 0 ? formatCurrency(basic) : "—"}
                          </span>
                        </div>
                        {ncb > 0 && (
                          <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                            <span>Non-Cash Benefits</span>
                            <span className="mono text-right font-medium">{formatCurrency(ncb)}</span>
                          </div>
                        )}
                        {customEarnings.map(
                          (e) =>
                            parseInputValue(e.amount) > 0 && (
                              <div key={e.id} className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                                <span>{e.label || "Other Earning"}</span>
                                <span className="mono text-right font-medium">{formatCurrency(parseInputValue(e.amount))}</span>
                              </div>
                            )
                        )}
                        <div className="grid grid-cols-[1fr_100px] gap-3 border-t border-[#DCE3EA] py-2.5 font-medium text-slate-950">
                          <span>Gross Salary</span>
                          <span className="mono text-right">
                            {grossSalary > 0 ? formatCurrency(grossSalary) : "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Statutory Deductions */}
                    <div className="relative mb-4">
                      <div className="mono mb-2 grid grid-cols-[1fr_100px] gap-3 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.08em] text-slate-400">
                        <span>Deductions</span>
                        <span className="text-right">Amount</span>
                      </div>
                      <div className="text-[12px] text-slate-700">
                        <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                          <span>PAYE</span>
                          <span className="mono text-right font-medium text-red-500">
                            {paye > 0 ? `−${formatCurrency(paye)}` : "—"}
                          </span>
                        </div>
                        <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                          <span>NSSF</span>
                          <span className="mono text-right font-medium text-red-500">
                            {nssf > 0 ? `−${formatCurrency(nssf)}` : "—"}
                          </span>
                        </div>
                        <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                          <span>SHIF</span>
                          <span className="mono text-right font-medium text-red-500">
                            {shif > 0 ? `−${formatCurrency(shif)}` : "—"}
                          </span>
                        </div>
                        <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                          <span>Housing Levy</span>
                          <span className="mono text-right font-medium text-red-500">
                            {housingLevy > 0 ? `−${formatCurrency(housingLevy)}` : "—"}
                          </span>
                        </div>
                        {pension > 0 && (
                          <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                            <span>Pension</span>
                            <span className="mono text-right font-medium text-red-500">
                              −{formatCurrency(pension)}
                            </span>
                          </div>
                        )}
                        {customDeductions.map(
                          (d) =>
                            parseInputValue(d.amount) > 0 && (
                              <div key={d.id} className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-2.5">
                                <span>{d.label || "Other Deduction"}</span>
                                <span className="mono text-right font-medium text-red-500">
                                  −{formatCurrency(parseInputValue(d.amount))}
                                </span>
                              </div>
                            )
                        )}
                        <div className="grid grid-cols-[1fr_100px] gap-3 border-t border-[#DCE3EA] py-2.5 font-medium text-slate-950">
                          <span>Total Deductions</span>
                          <span className="mono text-right text-red-600">
                            {totalDeductions > 0
                              ? `−${formatCurrency(totalDeductions)}`
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Net Pay */}
                    <div className="relative border-t-2 border-slate-950 pt-3">
                      <div className="flex items-baseline justify-between">
                        <span className="text-[12px] font-medium text-slate-950">Net Pay</span>
                        <span className="font-[family-name:var(--font-source-serif)] text-xl font-medium text-slate-950">
                          {calculation
                            ? `KSh ${formatCurrency(netPay)}`
                            : "—"}
                        </span>
                      </div>
                    </div>

                    {/* Tax Details */}
                    {calculation && (
                      <div className="relative mt-4 space-y-2 border-t border-[#E8E7E1] pt-3">
                        <div className="mono mb-1.5 text-[9px] uppercase tracking-[.13em] text-slate-400">
                          Tax details
                        </div>
                        <div className="grid grid-cols-[1fr_100px] gap-3 text-[12px]">
                          <span className="text-slate-500">Taxable Income</span>
                          <span className="mono text-right text-slate-700">
                            {formatCurrency(calculation.taxDetails.taxableIncome)}
                          </span>
                        </div>
                        <div className="grid grid-cols-[1fr_100px] gap-3 text-[12px]">
                          <span className="text-slate-500">Personal Relief</span>
                          <span className="mono text-right text-[#0F7B4D]">
                            −{formatCurrency(calculation.taxDetails.personalRelief)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Legal Notice */}
                    <div className="relative mt-4 border border-[#DCE3EA] bg-[#F3F6FA] px-3 py-2.5">
                      <p className="text-center text-[10px] leading-relaxed text-slate-500">
                        <span className="font-medium uppercase tracking-wide">Notice: </span>
                        This payslip is computer-generated by SekaniPay. It is legally
                        valid only when it bears an official company stamp issued by an
                        authorised signatory. An unstamped copy is for reference purposes
                        only and carries no legal weight.
                      </p>
                    </div>

                    {/* Footer */}
                    <div className="relative mt-3 border-t border-[#E8E7E1] pt-2 text-center">
                      <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-300">
                        Prepared by SekaniPay · sekanipay.com
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
